import React, { forwardRef, useCallback, memo, useMemo } from 'react';
import useGlobalState from '@hooks/useGlobalState';
import Box from '@wconcept/wconcept-ui-renewal/src/components/atoms/Box';
import { debounce } from 'lodash-es';
import type {
  LpProductCountResult,
  LpProductFilterType,
  LpProductRequest,
} from '@apis/renewal/lpProduct';
import { LpFilterProductListProps } from './types';
import { useCustomAd, useVirtualizedItems, useGridItems } from './hooks';
import {
  TopSection,
  VirtualizedList,
  DesktopProductList,
  EmptyState,
} from './components';
import InfiniteScrollList from './components/InfiniteScrollList';
import useLpFilterModalV2 from './hooks/useLpFilterModalV2';

interface LpFilterGlobalState {
  readonly assistData: {
    readonly isLogin: boolean;
  };
  readonly isMobile?: boolean;
  readonly appInfo: {
    readonly isNativeAppBoth: boolean;
  };
}

function LpFilterProductList(
  componentProps: LpFilterProductListProps,
  ref: React.ForwardedRef<HTMLDivElement>
) {
  const {
    totalElements,
    isPriority,
    totalPages,
    products,
    molocoAdLpProducts,
    molocoAdLpBaseData,
    isDesktop,
    hasNextPage,
    page,
    perPage = 60,
    sort,
    defalutSort,
    sorts,
    tooltip,
    useFreeDelivery,
    usePreOrder,
    useSoldOut,
    emptyText,
    preshowBrand,
    isCategoryMenu,
    tabSelectedValue,
    dcRateGroupInfo,
    dcRateValue,
    molocoAdProductsResult,
    similarNode,
    similarAfterItemCd,
    recommendBgColor,
    selectedFilters,
    originalFilters,
    filterBarList,
    lastFilterTabRef,
    virtual = true,
    categoryMenuSlot,
    mobileVirtualColumnCount,
    sortingBarToggleItem,
    fetchNextPage,
    onPageChange,
    onSortChange,
    onChangePreOrderOrFreeDelivery,
    onSoldoutChange,
    onClickProduct,
    onClickHeart,
    onResetFilter,
    onFilterSubmit,
    onDcRateGroupChange,
    onClickMolocoProductItem,
    requestProductAggByType,
    requestProductCount,
    onRetryFilter,
    onClickPromoBtn,
    onFilterClose,
    onClickViewStylVdClick,
    hideFilterArea,
    hideFilterOptionCounts,
    hiddenSelectedFilterTypes,
    hideLastPageButton,
    onClickMolocoAdLpProduct,
    showExclusiveEmblem,
    disableFilterScrollPin,
  } = componentProps;

  const { assistData, isMobile, appInfo } =
    useGlobalState<LpFilterGlobalState>();
  const { myHeartList } = useGlobalState();
  const isCustomAd = useCustomAd();
  const { openFilterModal } = useLpFilterModalV2(componentProps);
  const handleFilterRetry = useCallback(() => {
    onRetryFilter?.();
    openFilterModal();
  }, [onRetryFilter, openFilterModal]);
  const currentIsMobile = useMemo(() => {
    if (isDesktop || !isMobile) {
      return false;
    }
    return true;
  }, [isDesktop, isMobile]);
  const mobileVirtualCols = mobileVirtualColumnCount ?? 2;

  const virtualizedItems = useVirtualizedItems(
    products,
    isCustomAd,
    !!currentIsMobile,
    molocoAdLpProducts,
    molocoAdLpBaseData,
    molocoAdProductsResult,
    totalElements,
    mobileVirtualCols
  );
  // similar 노드가 있을 때만 삽입 기준 itemCd 전달 (빈 행 방지)
  const gridItems = useGridItems(
    virtualizedItems,
    mobileVirtualCols,
    similarNode != null ? similarAfterItemCd : undefined
  );

  // 매 렌더마다 새 debounce 인스턴스가 생기면 debounce 효과가 사라지고
  // Virtuoso의 endReached prop 참조도 매번 바뀌므로 useMemo로 안정화한다.
  const debouncedFetchNextPage = useMemo(
    () =>
      debounce(() => {
        if (hasNextPage) fetchNextPage();
      }, 150),
    [hasNextPage, fetchNextPage]
  );

  const getRequestProductAggByType = useCallback(
    (type: LpProductFilterType, params: LpProductRequest) =>
      requestProductAggByType(type, params),
    [requestProductAggByType]
  );

  const getRequestProductCount = useCallback(
    (params: LpProductRequest): Promise<LpProductCountResult | undefined> =>
      requestProductCount(params),
    [requestProductCount]
  );

  const renderMobileProductList = useCallback(() => {
    if (virtual) {
      return (
        <VirtualizedList
          gridItems={gridItems}
          similarNode={similarNode}
          mobileVirtualColumnCount={mobileVirtualCols}
          isMobile={!!currentIsMobile}
          assistData={assistData}
          myHeartList={myHeartList}
          appInfo={appInfo}
          totalCount={totalElements}
          onClickProduct={onClickProduct}
          onClickHeart={onClickHeart}
          onClickMolocoProductItem={onClickMolocoProductItem}
          recommendBgColor={recommendBgColor}
          hasNextPage={hasNextPage}
          debouncedFetchNextPage={debouncedFetchNextPage}
          shortFormPlayback={isMobile}
          isVideoPlay
          onClickViewStylVdClick={onClickViewStylVdClick}
          onClickMolocoAdLpProduct={onClickMolocoAdLpProduct}
          showExclusiveEmblem={showExclusiveEmblem}
        />
      );
    }
    return (
      <InfiniteScrollList
        products={products}
        isMobile={!!currentIsMobile}
        assistData={assistData}
        myHeartList={myHeartList}
        appInfo={appInfo}
        totalCount={totalElements}
        onClickProduct={onClickProduct}
        onClickHeart={onClickHeart}
        onClickMolocoProductItem={onClickMolocoProductItem}
        recommendBgColor={recommendBgColor}
        hasNextPage={hasNextPage}
        debouncedFetchNextPage={debouncedFetchNextPage}
        sort={sort}
        molocoAdProductList={molocoAdProductsResult}
        showExclusiveEmblem={showExclusiveEmblem}
      />
    );
  }, [
    virtual,
    products,
    currentIsMobile,
    assistData,
    myHeartList,
    appInfo,
    totalElements,
    onClickProduct,
    onClickHeart,
    onClickMolocoProductItem,
    recommendBgColor,
    hasNextPage,
    debouncedFetchNextPage,
    sort,
    molocoAdProductsResult,
    similarNode,
    gridItems,
    isMobile,
    onClickViewStylVdClick,
    mobileVirtualCols,
    showExclusiveEmblem,
    onClickMolocoAdLpProduct,
  ]);

  if (!products) return null;

  if (products.length === 0) {
    return (
      <Box className="bottom-product-wrap" ref={ref}>
        {categoryMenuSlot}
        <TopSection
          filterBarList={filterBarList}
          lastFilterTabRef={lastFilterTabRef}
          selectedFilters={selectedFilters}
          originalFilters={originalFilters}
          preshowBrand={preshowBrand}
          isCategoryMenu={isCategoryMenu}
          dcRateGroupInfo={dcRateGroupInfo}
          dcRateValue={dcRateValue}
          isNativeAppBoth={appInfo?.isNativeAppBoth}
          onResetFilter={onResetFilter}
          onFilterSubmit={onFilterSubmit}
          onDcRateGroupChange={onDcRateGroupChange}
          onClickPromoBtn={onClickPromoBtn}
          onFilterClose={onFilterClose}
          getRequestProductAggByType={getRequestProductAggByType}
          getRequestProductCount={getRequestProductCount}
          isMobile={!!isMobile}
          sort={sort}
          defalutSort={defalutSort}
          sorts={sorts}
          tooltip={tooltip}
          useFreeDelivery={useFreeDelivery}
          usePreOrder={usePreOrder}
          useSoldOut={useSoldOut}
          hideFilterArea={hideFilterArea}
          hideFilterOptionCounts={hideFilterOptionCounts}
          hiddenSelectedFilterTypes={hiddenSelectedFilterTypes}
          sortingBarToggleItem={sortingBarToggleItem}
          onChangePreOrderOrFreeDelivery={onChangePreOrderOrFreeDelivery}
          onSoldoutChange={onSoldoutChange}
          onSortChange={onSortChange}
          disableFilterScrollPin={disableFilterScrollPin}
        />
        <EmptyState
          products={products}
          selectedFilters={selectedFilters}
          emptyText={emptyText}
          onReset={handleFilterRetry}
        />
      </Box>
    );
  }

  return (
    <Box className="bottom-product-wrap" ref={ref}>
      {categoryMenuSlot}
      <TopSection
        filterBarList={filterBarList}
        lastFilterTabRef={lastFilterTabRef}
        selectedFilters={selectedFilters}
        originalFilters={originalFilters}
        preshowBrand={preshowBrand}
        isCategoryMenu={isCategoryMenu}
        dcRateGroupInfo={dcRateGroupInfo}
        dcRateValue={dcRateValue}
        onResetFilter={onResetFilter}
        onFilterSubmit={onFilterSubmit}
        onDcRateGroupChange={onDcRateGroupChange}
        onClickPromoBtn={onClickPromoBtn}
        onFilterClose={onFilterClose}
        getRequestProductAggByType={getRequestProductAggByType}
        getRequestProductCount={getRequestProductCount}
        isMobile={!!currentIsMobile}
        sort={sort}
        defalutSort={defalutSort}
        sorts={sorts}
        tooltip={tooltip}
        useFreeDelivery={useFreeDelivery}
        usePreOrder={usePreOrder}
        useSoldOut={useSoldOut}
        hideFilterArea={hideFilterArea}
        hideFilterOptionCounts={hideFilterOptionCounts}
        hiddenSelectedFilterTypes={hiddenSelectedFilterTypes}
        sortingBarToggleItem={sortingBarToggleItem}
        onChangePreOrderOrFreeDelivery={onChangePreOrderOrFreeDelivery}
        isNativeAppBoth={appInfo?.isNativeAppBoth}
        onSoldoutChange={onSoldoutChange}
        onSortChange={onSortChange}
        disableFilterScrollPin={disableFilterScrollPin}
      />
      {currentIsMobile ? (
        renderMobileProductList()
      ) : (
        <DesktopProductList
          products={products}
          isMobile={!!currentIsMobile}
          assistData={assistData}
          isPriority={isPriority}
          myHeartList={myHeartList}
          onClickProduct={onClickProduct}
          onClickHeart={onClickHeart}
          isCustomAd={isCustomAd}
          molocoAdProductsResult={molocoAdProductsResult}
          totalElements={totalElements}
          appInfo={appInfo}
          onClickMolocoProductItem={onClickMolocoProductItem}
          recommendBgColor={recommendBgColor}
          hasNextPage={hasNextPage}
          fetchNextPage={fetchNextPage}
          tabSelectedValue={tabSelectedValue}
          page={page}
          perPage={perPage}
          totalPages={totalPages}
          onPageChange={onPageChange}
          hideLastPageButton={hideLastPageButton}
          selectedFilters={selectedFilters}
          emptyText={emptyText}
          onReset={handleFilterRetry}
          onClickViewStylVdClick={onClickViewStylVdClick}
          showExclusiveEmblem={showExclusiveEmblem}
        />
      )}
    </Box>
  );
}

export default memo(
  forwardRef<HTMLDivElement, LpFilterProductListProps>(LpFilterProductList)
);
